import React from 'react';
import {
  Play,
  Pause,
  Square,
  Store,
  CheckCircle2,
  Clock,
  Ruler,
  MapPin,
  AlertCircle
} from 'lucide-react';
import DetailsModal from '../../../Presentation/components/common/DetailsModal';
import {AppCaption} from '../../../Presentation/components/ui/Typography';
import type {
  SessionTrackPoint,
  TrackType,
  WorkSession
} from '../models/session.model';
import {deriveSessionLifecycleState} from '../models/session.model';
import {useSessionTrack} from '../hooks/useSessionTrack';

interface SessionDetailsModalProps {
  isOpen: boolean;
  sessionId: number | null;
  /** Optional pre-loaded session — used as fallback header data while track loads. */
  fallbackSession?: WorkSession | null;
  onClose: () => void;
}

const SessionDetailsModal: React.FC<SessionDetailsModalProps> = ({
  isOpen,
  sessionId,
  fallbackSession = null,
  onClose
}) => {
  const {track, loading, error} = useSessionTrack(isOpen ? sessionId : null);
  const session = track?.session ?? fallbackSession;

  return (
    <DetailsModal
      isOpen={isOpen}
      onClose={onClose}
      title="تفاصيل الجلسة"
      width="max-w-3xl"
    >
      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !track && (
        <div className="py-12 text-center text-gray-500">جارٍ تحميل التفاصيل...</div>
      )}

      {session && (
        <div className="space-y-6">
          <SessionHeader session={session} />
          <SessionStats session={session} />
          <EventsTimeline markers={track?.markers ?? []} session={session} />
        </div>
      )}

      {!loading && !error && !session && (
        <div className="py-12 text-center text-gray-500">لا توجد بيانات لعرضها</div>
      )}
    </DetailsModal>
  );
};

export default SessionDetailsModal;

/* -------------------------------------------------------------------------- */
/* Header — name, date, lifecycle badge                                       */
/* -------------------------------------------------------------------------- */

const SessionHeader: React.FC<{session: WorkSession}> = ({session}) => {
  const state = deriveSessionLifecycleState(session);

  const badgeStyle =
    state === 'active'
      ? 'bg-green-100 text-green-800'
      : state === 'paused'
        ? 'bg-orange-100 text-orange-800'
        : 'bg-gray-100 text-gray-700';

  const badgeText =
    state === 'active' ? 'جلسة نشطة' : state === 'paused' ? 'موقوفة مؤقتاً' : 'منتهية';

  return (
    <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4">
      <div className="min-w-0">
        <div className="font-bold text-base leading-snug truncate">
          {session.marketerName ?? `مسوّق #${session.marketerId}`}
        </div>
        <AppCaption className="mt-1 block text-gray-500">{session.workDate}</AppCaption>
      </div>
      <span
        className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium ${badgeStyle}`}
      >
        {badgeText}
      </span>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Stats strip                                                                 */
/* -------------------------------------------------------------------------- */

const SessionStats: React.FC<{session: WorkSession}> = ({session}) => {
  const durationFallback =
    session.clockOutAt !== null
      ? Math.floor((session.clockOutAt.getTime() - session.clockInAt.getTime()) / 1000)
      : Math.floor((Date.now() - session.clockInAt.getTime()) / 1000);

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <StatBox
        icon={<Clock className="h-4 w-4 text-green-600" />}
        label="بدء الدوام"
        value={formatTime(session.clockInAt)}
      />
      <StatBox
        icon={<Clock className="h-4 w-4 text-red-600" />}
        label="نهاية الدوام"
        value={session.clockOutAt ? formatTime(session.clockOutAt) : '—'}
      />
      <StatBox
        icon={<MapPin className="h-4 w-4 text-gray-600" />}
        label="مدة العمل"
        value={formatDuration(session.durationSeconds, durationFallback)}
      />
      <StatBox
        icon={<Ruler className="h-4 w-4 text-blue-600" />}
        label="المسافة"
        value={formatDistanceKm(session.totalDistanceMeters)}
      />
      <StatBox
        icon={<Store className="h-4 w-4 text-purple-600" />}
        label="المتاجر"
        value={session.totalStoresVisited.toString()}
      />
    </div>
  );
};

interface StatBoxProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const StatBox: React.FC<StatBoxProps> = ({icon, label, value}) => (
  <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
    <div className="flex items-center gap-2 text-xs text-gray-600">
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-1 text-base font-semibold leading-tight">{value}</div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Events timeline                                                             */
/* -------------------------------------------------------------------------- */

interface TimelineRow {
  marker: SessionTrackPoint;
  index: number;
  prev: SessionTrackPoint | null;
}

const EventsTimeline: React.FC<{markers: SessionTrackPoint[]; session: WorkSession}> = ({
  markers,
  session
}) => {
  if (markers.length === 0) {
    return (
      <div>
        <div className="mb-3 font-bold text-base">سجل الأحداث</div>
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
          لا توجد أحداث مسجلة لهذه الجلسة
        </div>
      </div>
    );
  }

  // markers come from the API in chronological order; defensively sort.
  const sorted = [...markers].sort(
    (a, b) => a.recordedAt.getTime() - b.recordedAt.getTime()
  );

  const rows: TimelineRow[] = sorted.map((marker, index) => ({
    marker,
    index,
    prev: index === 0 ? null : sorted[index - 1]
  }));

  return (
    <div>
      <div className="mb-3 font-bold text-base">سجل الأحداث</div>
      <ol className="space-y-0">
        {rows.map((row) => (
          <TimelineItem
            key={`${row.marker.recordedAt.toISOString()}-${row.index}`}
            row={row}
            isLast={row.index === rows.length - 1}
            session={session}
          />
        ))}
      </ol>
    </div>
  );
};

interface TimelineItemProps {
  row: TimelineRow;
  isLast: boolean;
  session: WorkSession;
}

const TimelineItem: React.FC<TimelineItemProps> = ({row, isLast, session}) => {
  const meta = getMarkerMeta(row.marker, row.prev, session);

  return (
    <li className="flex items-stretch gap-3">
      {/* Dot + connecting line column (right side in RTL) */}
      <div className="flex flex-col items-center">
        <span
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full shadow-sm ${meta.dotClass}`}
        >
          {meta.icon}
        </span>
        {!isLast && <span className="w-px flex-1 bg-gray-200" />}
      </div>

      {/* Event content */}
      <div className={`flex-1 ${isLast ? 'pb-0' : 'pb-4'}`}>
        <div className="rounded-md border border-gray-100 bg-white p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="font-medium text-sm leading-snug">{meta.title}</div>
            <span className="flex-shrink-0 text-xs text-gray-500 tabular-nums">
              {formatTime(row.marker.recordedAt)}
            </span>
          </div>
          {meta.subtitle && (
            <div className="mt-1 text-xs text-gray-500">{meta.subtitle}</div>
          )}
        </div>
      </div>
    </li>
  );
};

interface MarkerMeta {
  title: string;
  subtitle: string | null;
  icon: React.ReactNode;
  dotClass: string;
}

function getMarkerMeta (
  marker: SessionTrackPoint,
  prev: SessionTrackPoint | null,
  session: WorkSession
): MarkerMeta {
  const type: TrackType = marker.trackType;

  switch (type) {
    case 'clock_in':
      return {
        title: 'بدء الدوام',
        subtitle: null,
        icon: <Play className="h-4 w-4 text-white" />,
        dotClass: 'bg-green-500'
      };

    case 'clock_out': {
      const isFinalized = session.finalizedAt !== null;
      return {
        title: isFinalized ? 'إنهاء الدوام' : 'إيقاف مؤقت',
        subtitle: null,
        icon: <Square className="h-4 w-4 text-white" />,
        dotClass: isFinalized ? 'bg-red-500' : 'bg-gray-500'
      };
    }

    case 'pause':
      return {
        title: 'استراحة',
        subtitle: 'إيقاف مؤقت للتتبع',
        icon: <Pause className="h-4 w-4 text-white" />,
        dotClass: 'bg-orange-500'
      };

    case 'resume': {
      const gap = prev
        ? formatGap(marker.recordedAt.getTime() - prev.recordedAt.getTime())
        : null;
      return {
        title: 'استئناف الدوام',
        subtitle: gap ? `بعد ${gap}` : null,
        icon: <Play className="h-4 w-4 text-white" />,
        dotClass: 'bg-green-500'
      };
    }

    case 'visit_start':
      return {
        title: marker.storeName ? `بدء زيارة — ${marker.storeName}` : 'بدء زيارة',
        subtitle: null,
        icon: <Store className="h-4 w-4 text-white" />,
        dotClass: 'bg-blue-500'
      };

    case 'visit_end':
      return {
        title: marker.storeName ? `انتهاء الزيارة — ${marker.storeName}` : 'انتهاء الزيارة',
        subtitle: null,
        icon: <CheckCircle2 className="h-4 w-4 text-white" />,
        dotClass: 'bg-blue-400'
      };

    case 'periodic':
    default:
      return {
        title: 'تحديث موقع',
        subtitle: null,
        icon: <MapPin className="h-4 w-4 text-white" />,
        dotClass: 'bg-gray-400'
      };
  }
}

/* -------------------------------------------------------------------------- */
/* Formatting                                                                  */
/* -------------------------------------------------------------------------- */

function formatTime (d: Date): string {
  return `${d.getHours().toString().padStart(2, '0')}:${d
    .getMinutes()
    .toString()
    .padStart(2, '0')}`;
}

function formatDistanceKm (meters: number | null): string {
  if (meters === null || meters === undefined) {return '—';}
  if (meters < 1) {return '0 كم';}
  return `${(meters / 1000).toFixed(2)} كم`;
}

function formatDuration (secs: number | null, fallback: number): string {
  const totalSeconds = secs ?? fallback;
  if (totalSeconds <= 0) {return '—';}
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  return `${h} س ${m} د`;
}

function formatGap (ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60000));
  if (minutes < 60) {return `${minutes} دقيقة`;}
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) {return `${h} ساعة`;}
  return `${h} س ${m} د`;
}
